import classNames from 'classnames';
import { Flex, Tag, Typography } from 'antd';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import BaseButton from 'components/atoms/baseButton';
import CHFRenalStatus from 'components/molecules/activity-table/components/chf-renal-status';
import RecoveredStatus from 'components/molecules/activity-table/components/date-col/recovered-status';
import ErroneousWrapper from 'components/molecules/activity-table/components/erroneous-modal';
import AssignSwitch from 'components/molecules/activity-table/components/assign-switch';
import { getActivityStatus } from 'utils';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import {
  convertDateToTimeZone,
  DateFormatter,
  isCombinedModule,
} from 'infrastructure/functions';
import { useGetDoNotCallInfo } from 'infrastructure/hooks/control-panel/use-get-call-info';
import {
  useAddActivityComment,
  useGenAiComment,
} from 'infrastructure/hooks/rpm';
import { getPatientAssignQueryKey } from 'infrastructure/hooks/rpm/assign-tool/use-get-patient-assing';
import { useAssignPatient } from 'infrastructure/hooks/rpm/assign-tool/use-set-assign-patient';
import { useCountdownTimer } from 'infrastructure/hooks/use-count-down-timer';
import useUser from 'utils/useUser';
import { useAppSelector } from 'store';
import {
  ActivityStatus,
  DateFormats,
  ReadingStatus,
  type ModuleType,
} from 'infrastructure/enums';
import { isCHFReading } from 'utils/get-reading-info';

import PatientActivityList from './patient-activity-list';
import CommentSection from '../comment-section';
import ReviewedButton from './reviewed-button';
import s from './styles.module.scss';

import type { FC } from 'react';
import type { IActivityData } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type {
  TRpmDashboardActivityQueryKey,
  TRpmPatientActivityQueryKey,
} from 'infrastructure/hooks/rpm';
import type { TView } from 'components/molecules/activity-table/types';
import type { IPatientAssignStateChange } from 'infrastructure/interfaces';

const { Text } = Typography;

interface ActivityCardProps {
  activityData: IActivityData;
  showReviewCol: boolean;
  view: TView;
  getActivityQueryKey:
    | TRpmDashboardActivityQueryKey
    | TRpmPatientActivityQueryKey;
  tableTypePrefix?: 'a' | 'u';
  module: ModuleType;
  hideReviewButton?: boolean;
  type?: ActivityStatus;
  className?: string;
}

const ActivityCard: FC<ActivityCardProps> = ({
  activityData,
  showReviewCol,
  view,
  getActivityQueryKey,
  tableTypePrefix,
  module,
  hideReviewButton,
  type,
  className,
}) => {
  if (!activityData) return null;

  const { doNotCallRpm } = useGetDoNotCallInfo();
  const { isReadOnlyRole } = useCheckRoleAccess();

  const {
    isMedicalAssistantRole,
    userType,
    hasClinicalRole,
    isMobileDevice,
    loginGuid,
    isAgencyDoctor,
    isFullyManagedAgency,
    isCriticalOrAtRiskAlertingRole,
  } = useUser();

  const {
    globalStatus,
    isRecovered,
    thresholdName,
    mightBeErroneousReason,
    recoveredByReadingId,
    patient,
    value,
    statuses,
    readingType,
    deviceId,
    id,
    isErroneous,
    comments,
    reviewed,
    createdAt,
  } = activityData;

  const queryClient = useQueryClient();
  const [isCommentExpanded, setIsCommentExpanded] = useState(isErroneous);
  const [checked, setChecked] = useState(false);

  const idx = tableTypePrefix
    ? `${tableTypePrefix}-${activityData.deviceId}-${activityData.id}`
    : `${activityData.deviceId}-${activityData.id}`;

  const { addActivityComment } = useAddActivityComment({
    getActivityQueryKey,
    patientGuid: activityData.patient?.guid,
    tableRowIdx: idx,
  });

  const { assignPatient, isLoading: isAssignLoading } = useAssignPatient();

  const actions = useAppSelector((state) => state.activityActions.actions[idx]);

  const { t } = useTranslation();
  const location = useLocation();
  const { slaReadings } = queryString.parse(location.search);

  const status = slaReadings === 'true' ? 'slaReading' : globalStatus;
  const isCHF = isCHFReading(thresholdName);

  const showCommentColumn = isCombinedModule(module) ? !isReadOnlyRole : true;
  const disableForMARole =
    globalStatus === ReadingStatus.Critical &&
    isMedicalAssistantRole &&
    !doNotCallRpm;

  const isReviewed = useMemo(() => {
    const reviewedByUserType = comments.find((c) => c.reviewed)?.userType;
    return reviewedByUserType === userType && reviewed;
  }, [comments.length]);

  const { data: aiData } = useGenAiComment({
    patientGuid: patient.guid,
    tableRowIdx: idx,
  });

  const submitCommentLockByAi =
    Boolean(actions?.message) &&
    aiData?.result.comment === actions?.message &&
    hasClinicalRole;

  const assignQueryKey = getPatientAssignQueryKey(patient.guid);
  const { assignee } =
    queryClient.getQueryData<IPatientAssignStateChange>(assignQueryKey) ?? {};

  const showAssign =
    !isMobileDevice && isAgencyDoctor && !isCriticalOrAtRiskAlertingRole;

  const hideCommentArea =
    view === 'patient' &&
    isFullyManagedAgency &&
    isAgencyDoctor &&
    type === ActivityStatus.UnactionedNormalRisk;
  const hideCommentInput =
    !showCommentColumn || hideCommentArea || (isErroneous as boolean);

  const showErroneousBtnForAdminView = view === 'admin' && type === undefined;
  const showErroneousBtnForPatientView =
    view === 'patient' &&
    type &&
    type !== ActivityStatus.Erroneous &&
    !isErroneous;
  const showErroneousBtn =
    showErroneousBtnForAdminView || showErroneousBtnForPatientView;

  useEffect(() => {
    setChecked(!!assignee);
  }, [assignee]);

  const endTime = assignee?.expireAtTimestamp
    ? new Date(assignee?.expireAtTimestamp)
    : undefined;

  useCountdownTimer({
    endTime,
    onFinish: () => {
      if (assignee) {
        queryClient.setQueryData<IPatientAssignStateChange>(
          assignQueryKey,
          (oldData) => {
            if (!oldData) return;

            const newData = structuredClone(oldData);
            newData.assignee = undefined;

            return newData;
          },
        );
      }
    },
  });

  return (
    <Flex className={classNames(s.wrapper, className)} gap={40}>
      <Flex vertical>
        <p
          data-cy="reading-status"
          className={classNames(s[`status-${status}`], s.status)}
        >
          {getActivityStatus(status)}
        </p>
        {!isRecovered && thresholdName && isCHF && (
          <CHFRenalStatus
            thresholdName={thresholdName}
            thresholds={patient?.threshold}
            mightBeErroneousReason={mightBeErroneousReason}
          />
        )}
        {isRecovered && (
          <RecoveredStatus
            recoveredByReadingId={recoveredByReadingId}
            patientGuid={patient?.guid}
          />
        )}
      </Flex>
      <Flex vertical flex={1}>
        <Flex gap={12}>
          <PatientActivityList
            patientGuid={patient.guid}
            globalStatus={globalStatus}
            values={value}
            statuses={statuses}
            readingType={readingType}
            thresholds={patient.threshold}
          />
          <Flex
            gap={12}
            justify={
              isErroneous || type === ActivityStatus.Actioned
                ? 'space-between'
                : 'flex-end'
            }
            flex={1}
          >
            <Flex gap={12}>
              {type === ActivityStatus.Actioned && (
                <Tag
                  icon={<CheckCircleOutlined />}
                  color="success"
                  className={s.tag}
                >
                  {t('labels.reviewed')}
                </Tag>
              )}
              {isErroneous && (
                <Tag
                  icon={<CloseCircleOutlined />}
                  className={classNames(s.tag, s['tag-erroneous'])}
                >
                  {t('labels.erroneous')}
                </Tag>
              )}
            </Flex>
            <Flex gap={8} align="center" className={s['action-button-wrapper']}>
              {showAssign && (
                <AssignSwitch
                  checked={checked}
                  isLoading={isAssignLoading}
                  loginGuid={loginGuid}
                  assignee={assignee}
                  onChange={() => {
                    assignPatient(
                      {
                        patientGuid: patient.guid,
                        action: checked ? 'unassign' : 'assign',
                      },
                      {
                        onSuccess() {
                          setChecked((prev) => !prev);
                        },
                      },
                    );
                  }}
                />
              )}
              {!hideCommentInput && (
                <BaseButton
                  type="secondary"
                  className={s['action-button']}
                  label={t('labels.comment')}
                  icon={<MessageOutlined />}
                  onClick={() => setIsCommentExpanded(!isCommentExpanded)}
                />
              )}
              {showReviewCol && showCommentColumn && (
                <>
                  {!isErroneous && !hideReviewButton && (
                    <ReviewedButton
                      addComment={addActivityComment}
                      idx={idx}
                      deviceId={deviceId}
                      readingId={id}
                      currentInteractionStatus={patient.interactionStatus}
                      isReviewed={isReviewed}
                      submitCommentLockByAi={submitCommentLockByAi}
                      disabled={disableForMARole}
                      getActivityQueryKey={getActivityQueryKey}
                    />
                  )}
                  {showErroneousBtn && (
                    <ErroneousWrapper
                      getActivityQueryKey={getActivityQueryKey}
                      deviceId={deviceId}
                      readingId={id}
                      isErroneous={isErroneous}
                      disabled={disableForMARole}
                    />
                  )}
                </>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Flex gap={32} className={s['time-wrapper']}>
          <Text>
            {DateFormatter({
              date: new Date(parseInt(createdAt)),
              format: DateFormats['MM/dd/yyyy hh:mm:ss a'],
            })}
          </Text>
          <Text>
            {DateFormatter({
              date: convertDateToTimeZone(
                new Date(parseInt(createdAt)),
                patient?.timezone,
              ),
              format: DateFormats['MM/dd/yyyy hh:mm:ss a'],
            })}
            <span className={s['patient-timezone']}>
              {' '}
              ({t('labels.patientTimezone')})
            </span>
          </Text>
        </Flex>
        <CommentSection
          idx={
            tableTypePrefix
              ? `${tableTypePrefix}-${deviceId}-${id}`
              : `${deviceId}-${id}`
          }
          activityData={activityData}
          showReviewCol={showReviewCol}
          getActivityQueryKey={getActivityQueryKey}
          view={view}
          isExpanded={isCommentExpanded as boolean}
          setIsExpanded={setIsCommentExpanded}
          hideCommentInput={hideCommentInput}
        />
      </Flex>
    </Flex>
  );
};

export default ActivityCard;
